import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { HelpState, Sidebar } from '@enums';
import { TranslateService } from '@ngx-translate/core';
import { Help, Guide, HelpContextualInfo } from '@model';

import * as fromHelpStore from '@appStore/help';
import * as fromAppStore from '@appStore/app';
import { filter, take } from 'rxjs/operators';
import { SubSink } from 'subsink';

@Component({
    selector: 'app-help',
    templateUrl: './help.component.html',
    standalone: false
})
export class HelpComponent implements OnInit, OnDestroy {
  currentHelpState$: Observable<HelpState>;
  currentHelpContextualInfo$: Observable<HelpContextualInfo>;
  glossarySearchTerm$: Observable<string>;
  glossaryTagSearchTerm$: Observable<string>;
  isHelpLoading$: Observable<boolean>;
  helpData$: Observable<Help>;
  areGuidesLoading$: Observable<boolean>;
  guides$: Observable<Guide[]>;
  title = '';
  helpStateEnum = HelpState;

  private subs = new SubSink();

  constructor(
    private helpStore: Store<fromHelpStore.State>, 
    private appStore: Store<fromAppStore.State>,
    private translate: TranslateService) 
    {}

  ngOnInit() {
    this.subs.sink = this.helpStore
      .select(fromHelpStore.getHelpLoaded)
      .pipe(
        filter(x => !x),
        take(1)
      )
      .subscribe(isLoaded => {
        this.helpStore.dispatch(new fromHelpStore.HelpLoad());
      });

    this.subs.sink = this.helpStore
      .select(fromHelpStore.getGuidesLoaded)
      .pipe(
        filter(x => !x),
        take(1)
      )
      .subscribe(isLoaded => {
        this.helpStore.dispatch(new fromHelpStore.GuidesLoad());
      });

    this.currentHelpState$ = this.helpStore.select(fromHelpStore.getCurrentHelpState);
    this.currentHelpContextualInfo$ = this.helpStore.select(fromHelpStore.getCurrentContextualInfo);
    this.glossarySearchTerm$ = this.helpStore.select(fromHelpStore.getGlossarySearchTerm);
    this.glossaryTagSearchTerm$ = this.helpStore.select(fromHelpStore.getGlossaryTagSearchTerm);
    this.isHelpLoading$ = this.helpStore.select(fromHelpStore.getHelpLoading);
    this.helpData$ = this.helpStore.select(fromHelpStore.getAllHelp);

    this.subs.sink = this.currentHelpState$.pipe(filter(x => !!x)).subscribe(state => {
      this.setTitle(state);
    });

    this.areGuidesLoading$ = this.helpStore.select(fromHelpStore.getGuidesLoading);
    this.guides$ = this.helpStore.select(fromHelpStore.getAllGuides);
  }

  onClose() {
    this.helpStore.dispatch(new fromHelpStore.HelpSetState(HelpState.closed));
    this.appStore.dispatch(new fromAppStore.InactivateSidebar(Sidebar.Help));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  setTitle(state: HelpState) {
    switch (state) {
      case HelpState.contextualInfo: {
        this.title = this.translate.instant('core.help.menu.contextualInfo');
        break;
      }
      case HelpState.glossary: {
        this.title = this.translate.instant('core.help.menu.glossary');
        break;
      }
      case HelpState.introduction: {
        this.title = this.translate.instant('core.help.menu.introduction');
        break;
      }
      case HelpState.support: {
        this.title = this.translate.instant('core.help.menu.support');
        break;
      }
      default: {
        this.title = this.translate.instant('core.help.defaultTitle');
        break;
      }
    }
  }
}
